import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '../api/client';
import { useAuth, type AuthUser } from '../store/auth';
import { endpoints } from '../api/endpoints';
import { PhoneInput } from '../components/PhoneInput';
import { SmsCodeInput } from '../components/SmsCodeInput';
import { ConsentNotice } from '../components/ConsentNotice';
import { SpecialConsentNotice } from '../components/SpecialConsentNotice';

type Step = 'phone' | 'code';

// Ответ /auth/sms/verify — сессия, которую кладём в стор.
interface VerifyResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

const PHONE_RE = /^\+7\d{10}$/;

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const setConsentRequired = useAuth((s) => s.setConsentRequired);
  const setSpecialConsent = useAuth((s) => s.setSpecialConsent);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  // Вторая (необязательная) галочка — согласие на спец-категории (Часть 2: здоровье/диета).
  const [special, setSpecial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Любую ошибку приводим к человекочитаемому тексту: для ApiException — message
  // из тела ответа сервера (он на русском), иначе общий фоллбэк.
  function messageOf(e: unknown): string {
    if (e instanceof ApiException) return e.body.message;
    return 'Что-то пошло не так. Попробуйте позже.';
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    if (!PHONE_RE.test(phone)) {
      setError('Введите корректный номер телефона');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/sms/send', { phone });
      setStep('code');
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const session = await api.post<VerifyResponse>('/auth/sms/verify', { phone, code });
      setSession(session);
      // Галочки согласия стояли на шаге телефона → фиксируем серверно, чтобы гейт не всплыл.
      // special — вторая (необязательная) галочка: спец-категории (здоровье/диета).
      try {
        await endpoints.acceptConsent(special);
        setConsentRequired(false);
        setSpecialConsent(special);
      } catch {
        // Не блокируем вход: если не сохранилось — ConsentScreen поймает при следующем заходе.
      }
      navigate('/');
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }

  function changeNumber() {
    setStep('phone');
    setCode('');
    setError(null);
  }

  // СМС-вход на паузе на время закрытого теста (нет боевого ключа sms.ru): приложение
  // открывается через Telegram-бота — вход без кода (по initData). Вся СМС-логика ниже
  // сохранена; вернуть вход по телефону → собрать фронт с VITE_SMS_LOGIN=1.
  const smsEnabled = import.meta.env.VITE_SMS_LOGIN === '1';
  if (!smsEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <h1 className="mb-4 text-2xl font-bold text-primary-600">Вход в Рецепт.ИИ</h1>
          <p className="mb-6 text-[15px] leading-relaxed text-gray-600">
            Сейчас приложение открывается через Telegram — вход без кода. Откройте нашего бота
            и нажмите «Запустить».
          </p>
          <a
            href="https://t.me/recipt_help_Bot"
            target="_blank"
            rel="noopener"
            className="block w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white transition hover:bg-primary-700"
          >
            Открыть в Telegram
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-primary-600 mb-6 text-center">
          Вход в Рецепт.ИИ
        </h1>

        {step === 'phone' ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <PhoneInput value={phone} onChange={setPhone} disabled={loading} />
            <label className="flex items-start gap-2 text-left text-[13px] leading-snug text-gray-600">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-none accent-primary-600"
              />
              <span>
                <ConsentNotice />
              </span>
            </label>
            <label className="flex items-start gap-2 text-left text-[13px] leading-snug text-gray-600">
              <input
                type="checkbox"
                checked={special}
                onChange={(e) => setSpecial(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-none accent-primary-600"
              />
              <span>
                <SpecialConsentNotice />
              </span>
            </label>
            <button
              type="submit"
              disabled={loading || phone === '' || !agreed}
              className="w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Получить код
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Код отправлен на {phone}
            </p>
            <SmsCodeInput value={code} onChange={setCode} disabled={loading} />
            <button
              type="submit"
              disabled={loading || code.length !== 4}
              className="w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Войти
            </button>
            <button
              type="button"
              onClick={changeNumber}
              disabled={loading}
              className="w-full text-sm text-gray-500 transition hover:text-gray-700 disabled:text-gray-300"
            >
              Изменить номер
            </button>
          </form>
        )}

        {error && (
          <p data-testid="login-error" className="mt-4 text-sm text-red-600 text-center">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
